// src/dueInfoQuestions.ts
import { checkHql, evaluateHql } from "@rock/hql";
import {
  infoQuestionId
} from "@rock/types";
import { isTestId } from "@rock/util";

// src/questionStands.ts
import { equipmentIdentity, normalizeBrandName, normalizeIdentifier } from "@rock/util";
function questionStands(question, facts) {
  switch (question.kind) {
    case "equipment-brand": {
      const unit = facts.equipment.find((equipment) => equipment.id === question.equipmentId);
      return question.options.length > 1 && !!unit?.active && sameAskedFor(brandQuestionAskedFor(unit), question.askedFor);
    }
    case "info":
      return !isInfoAnswered(facts.systemInfo, question.infoTypeId);
  }
}
function brandQuestionAskedFor(unit) {
  const identity = equipmentIdentity(unit);
  if (!identity) return void 0;
  const brand = unit.fields?.brand ? normalizeBrandName(unit.fields.brand).key : "";
  const brandId = unit.fields?.brandRef?.brandId;
  return {
    identity: normalizeIdentifier(identity.typed),
    ...brand ? { brand } : {},
    ...brandId ? { brandId } : {}
  };
}
function isAskableInfoType(infoType) {
  return infoType.dataType === "boolean" || infoType.dataType === "select" && !!infoType.options?.length;
}
function isInfoAnswered(systemInfo, infoTypeId) {
  return systemInfo.some((info) => info.typeId === infoTypeId && info.active && hasValue(info.inputValue));
}
function sameAskedFor(current, asked) {
  return !!current && current.identity === asked.identity && current.brand === asked.brand && current.brandId === asked.brandId;
}
function hasValue(value) {
  if (value === void 0 || value === null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

// src/dueInfoQuestions.ts
function dueInfoQuestions(input) {
  const byId = new Map(input.existing.map((question) => [question.id, question]));
  const brokenWhen = [];
  const ask = input.infoTypes.filter((infoType) => !byId.has(infoQuestionId(infoType.id)) && shouldAsk(infoType, input, brokenWhen)).map((infoType) => infoQuestion(infoType, input));
  const answered = input.existing.filter(
    (question) => question.kind === "info" && question.status === "open" && isInfoAnswered(input.systemInfo, question.infoTypeId)
  ).map((question) => question.id);
  return { ask, answered, brokenWhen };
}
function startsAskingExistingHomes(before, after) {
  if (!after || !asksExistingHomes(after)) return false;
  if (!before || !asksExistingHomes(before)) return true;
  return (before.askExistingHomes.when ?? "").trim() !== (after.askExistingHomes.when ?? "").trim();
}
function shouldAsk(infoType, input, brokenWhen) {
  if (!asksExistingHomes(infoType)) return false;
  if (!finishedWithoutSkipping(input.home, infoType)) return false;
  if (isInfoAnswered(input.systemInfo, infoType.id)) return false;
  const when = infoType.askExistingHomes.when;
  if (!when?.trim()) return true;
  const { compiled } = checkHql(when, input.catalog);
  if (!compiled) brokenWhen.push(infoType.id);
  return !!compiled && evaluateHql(compiled, input.doc, input.now).status === "applies";
}
function finishedWithoutSkipping(home, infoType) {
  const summary = home.spaceSummary[infoType.spaceId];
  return summary?.status === "finished" && summary.finishedVia !== "skipped";
}
function infoQuestion(infoType, input) {
  const now = input.now.toISOString();
  return {
    object: "home-question",
    schema: 1,
    id: infoQuestionId(infoType.id),
    homeId: input.home.id,
    status: "open",
    createdAt: now,
    updatedAt: now,
    test: isTestId(input.home.id),
    kind: "info",
    infoTypeId: infoType.id,
    spaceId: infoType.spaceId
  };
}
function asksExistingHomes(infoType) {
  return !!infoType.askExistingHomes && isAskableInfoType(infoType);
}

// src/planBrandQuestion.ts
import {
  equipmentBrandQuestionId
} from "@rock/types";
import { isTestId as isTestId2 } from "@rock/util";
function planBrandQuestion(input) {
  const { existing, unit, options } = input;
  const askedFor = brandQuestionAskedFor(unit);
  if (!askedFor) return void 0;
  const standing = existing?.kind === "equipment-brand" && existing.status === "open" && sameAskedFor(askedFor, existing.askedFor) && brandIdsOf(existing.options) === brandIdsOf(options);
  if (standing) return { keep: true };
  const now = input.now.toISOString();
  return {
    write: {
      object: "home-question",
      schema: 1,
      id: equipmentBrandQuestionId(unit.id),
      homeId: unit.homeId,
      status: "open",
      createdAt: now,
      updatedAt: now,
      test: isTestId2(unit.homeId),
      kind: "equipment-brand",
      equipmentId: unit.id,
      askedFor,
      options
    }
  };
}
function settleBrandQuestion(question, unit) {
  if (question?.kind !== "equipment-brand") return void 0;
  const stands = !!unit?.active && sameAskedFor(brandQuestionAskedFor(unit), question.askedFor);
  if (question.status === "stale") return stands ? "reopen" : void 0;
  if (question.status !== "open" || stands) return void 0;
  const picked = unit?.fields?.brandRef?.brandId;
  return picked && question.options.some((option) => option.brandId === picked) ? "answered" : "stale";
}
function brandIdsOf(options) {
  return options.map((option) => option.brandId).sort().join("\n");
}

// src/visibleQuestions.ts
var KIND_ORDER = { "equipment-brand": 0, info: 1 };
function visibleQuestions(questions, facts, now, opts = {}) {
  return standingQuestions(questions, facts).filter((question) => !isSnoozed(question, now)).slice(0, opts.cap ?? questions.length);
}
function standingQuestions(questions, facts) {
  return questions.filter((question) => question.status === "open" && questionStands(question, facts)).sort(
    (a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id)
  );
}
function isSnoozed(question, now) {
  return !!question.snoozedUntil && new Date(question.snoozedUntil).getTime() > now.getTime();
}
export {
  brandQuestionAskedFor,
  dueInfoQuestions,
  isAskableInfoType,
  isInfoAnswered,
  isSnoozed,
  planBrandQuestion,
  questionStands,
  sameAskedFor,
  settleBrandQuestion,
  standingQuestions,
  startsAskingExistingHomes,
  visibleQuestions
};
//# sourceMappingURL=index.js.map